import { z } from 'zod/v4';
import { trpc } from '@org/clients/school';
import { DynamicForm, Switch } from '@org/ui';
import { toast } from 'sonner';
import { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getMutationErrorMessage } from '../shared/multi-step-utils';

const learningPathSchema = z.object({
  pathId: z.string().optional(),
  name: z.string().min(1, "Name is required").describe('learningPaths.form.labels.name'),
  description: z
    .string()
    .max(2000, "Description must be 2000 characters or less")
    .describe('learningPaths.form.labels.description')
    .optional()
    .nullable(),
  isActive: z.boolean().default(true).describe('learningPaths.form.labels.isActive'),
  isDefault: z.boolean().default(false).describe('learningPaths.form.labels.isDefault'),
});

type LearningPathFormValues = z.infer<typeof learningPathSchema>;

export interface LearningPathFormProps {
  defaultValues: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
  hideButtons?: boolean;
  /** Fires when the fields differ from the last saved values (and when they are back in sync). */
  onDirtyChange?: (dirty: boolean) => void;
}

export interface LearningPathFormRef {
  submit: () => void;
}

export const LearningPathForm = forwardRef<LearningPathFormRef, LearningPathFormProps>(({
  defaultValues,
  onSubmit,
  onClose,
  hideButtons,
  onDirtyChange
}, ref) => {
  const utils = trpc.useUtils();
  const pathId = defaultValues?.pathId;
  // A path that is already the default cannot be un-defaulted here: exactly
  // one default must exist, so the admin promotes another path instead.
  const wasDefault = defaultValues?.isDefault === true;
  const { t } = useTranslation('admin');

  const [formDefaultValues, setFormDefaultValues] = useState({
    isActive: true,
    isDefault: false,
    ...defaultValues,
  });
  const formRef = useRef<HTMLFormElement>(null);

  const invalidate = async () => {
    await utils.learningPaths.list.invalidate();
    if (pathId) {
      await utils.learningPaths.getById.invalidate({ pathId });
    }
  };

  const createMutation = trpc.learningPaths.create.useMutation();
  const updateMutation = trpc.learningPaths.update.useMutation();
  const setDefaultMutation = trpc.learningPaths.setDefault.useMutation();

  useImperativeHandle(ref, () => ({
    submit: () => {
      if (formRef.current) {
        formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  }));

  const handleFormSubmit = async (data: LearningPathFormValues) => {
    try {
      let savedPathId: string | undefined = pathId;
      const payload = {
        name: data.name,
        description: data.description ?? null,
        isActive: data.isActive,
      };

      if (pathId) {
        await updateMutation.mutateAsync({ pathId, ...payload });
      } else {
        const created = await createMutation.mutateAsync(payload);
        savedPathId = created.pathId;
      }

      if (data.isDefault && !wasDefault && savedPathId) {
        await setDefaultMutation.mutateAsync({ pathId: savedPathId });
      }

      await invalidate();
      toast.success(
        pathId
          ? t('learningPaths.form.toasts.updateSuccess')
          : t('learningPaths.form.toasts.createSuccess'),
      );
      const saved = { ...data, pathId: savedPathId, isDefault: wasDefault || data.isDefault };
      // The saved values become the new baseline, so the form reads as clean.
      setFormDefaultValues(saved);
      onSubmit(saved);
    } catch (error) {
      console.error("Error saving learning path:", error);
      toast.error(t('learningPaths.form.toasts.saveFailed', { message: getMutationErrorMessage(error) }));
    }
  };

  const labelMappings = {
    name: t('learningPaths.form.labels.name'),
    description: t('learningPaths.form.labels.description'),
    isActive: t('learningPaths.form.labels.isActive'),
    isDefault: t('learningPaths.form.labels.isDefault'),
  };

  const hiddenFields = ['pathId'];

  const customRenderers = {
    isDefault: ({ field }: { field: { value: unknown; onChange: (value: unknown) => void } }) => (
      <div className="flex items-center gap-3">
        <Switch
          checked={wasDefault || Boolean(field.value)}
          disabled={wasDefault}
          onCheckedChange={(checked) => field.onChange(checked)}
        />
        <span className="text-sm text-muted-foreground">
          {wasDefault
            ? t('learningPaths.form.defaultHintLocked')
            : t('learningPaths.form.defaultHint')}
        </span>
      </div>
    ),
  };

  return (
    <DynamicForm
      schema={learningPathSchema}
      onSubmit={handleFormSubmit}
      defaultValues={formDefaultValues}
      isEditing={!!pathId}
      onClose={onClose}
      labelMappings={labelMappings}
      hiddenFields={hiddenFields}
      hideButtons={hideButtons}
      formRef={formRef}
      customRenderers={customRenderers as any}
      onDirtyChange={onDirtyChange}
    />
  );
});
